import { UserRole } from "@excel-center/shared";
import { LogOut, Settings } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { AUTH_ROUTES } from "@/src/auth/constants/auth-routes.constants";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { logout } from "@/src/auth/store/auth.thunks";
import { TopBar } from "@/src/common/components/top-bar";
import { ThemeSwitcher } from "@/src/common/components/top-bar/theme-switcher";
import { Drawer } from "@/src/common/components/ui";
import { Button } from "@/src/common/components/ui/button";
import { Tabs } from "@/src/common/components/ui/tabs";
import { useTopBarExtraItems } from "@/src/common/hooks/use-top-bar-extra-items";
import { useAppDispatch } from "@/src/common/redux/store";
import { JOBS_ROUTES } from "@/src/jobs/constants/jobs-routes.constants";
import { POST_ROUTES } from "@/src/post/constants/post-routes.constants";
import { SETTINGS_ROUTES } from "@/src/settings/settings-routes.constants";
import { STUDENT_CENTRAL_ROUTES } from "@/src/student-central/constants/student-central-routes.constants";
import { clsx } from "../utils/clsx";
import { getInitials } from "../utils/get-initials";

const NAV_PATHS = [POST_ROUTES.feed, STUDENT_CENTRAL_ROUTES.studentCentral, JOBS_ROUTES.jobs] as const;
type NavPath = (typeof NAV_PATHS)[number];

const NAV_LABEL_KEY = {
	[POST_ROUTES.feed]: "nav.feed",
	[STUDENT_CENTRAL_ROUTES.studentCentral]: "nav.student_central",
	[JOBS_ROUTES.jobs]: "nav.jobs",
} as const satisfies Record<NavPath, "nav.feed" | "nav.student_central" | "nav.jobs">;

/** Signed-in visitors navigate back into the authed shell, not the public twin. */
const NAV_APP_TARGET = {
	[POST_ROUTES.feed]: POST_ROUTES.appFeed,
	[STUDENT_CENTRAL_ROUTES.studentCentral]: STUDENT_CENTRAL_ROUTES.appStudentCentral,
	[JOBS_ROUTES.jobs]: JOBS_ROUTES.appJobs,
} as const satisfies Record<NavPath, string>;

function resolveSelectedTab(pathname: string): NavPath | undefined {
	return NAV_PATHS.find((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * Shell for unauthenticated / guest pages (Feed, Student Central, Jobs R/O,
 * Log in). Branded top bar with public nav + CTAs, then outlet.
 */
export function PublicLayout() {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const { profile, role } = useAuth();
	const extraItems = useTopBarExtraItems(role);
	const selectedKey = resolveSelectedTab(pathname);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const hideTopBar = pathname === AUTH_ROUTES.login || pathname === "/otp";
	const hideSettingsItem = role === UserRole.STUDENT;

	const topBarUser = useMemo(() => {
		if (!profile) return undefined;
		const fullName = `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim();
		const name = fullName || profile.email;
		return { name, initials: getInitials(fullName || null, profile.email), avatarUrl: profile.avatar_url };
	}, [profile]);

	const navTargetFor = (path: NavPath) => (profile ? NAV_APP_TARGET[path] : path);

	const goAndClose = (to: string) => {
		setIsMenuOpen(false);
		navigate(to);
	};

	const signOutAndClose = () => {
		setIsMenuOpen(false);
		void dispatch(logout());
	};

	const nav = (
		<Tabs variant="secondary" selectedKey={selectedKey}>
			<Tabs.ListContainer>
				<Tabs.List aria-label={t("aria.primary_navigation")} className={clsx("whitespace-nowrap border-b-0")}>
					{NAV_PATHS.map((path) => (
						<Tabs.Tab
							key={path}
							id={path}
							render={(domProps: object) => <Link {...domProps} to={navTargetFor(path)} />}
							className={clsx(selectedKey === path && "text-primary-600 font-semibold", "hover:text-primary-600")}
						>
							{t(NAV_LABEL_KEY[path])}
							<Tabs.Indicator />
						</Tabs.Tab>
					))}
				</Tabs.List>
			</Tabs.ListContainer>
		</Tabs>
	);

	const actions = topBarUser ? undefined : (
		<div className="hidden items-center gap-3 md:flex">
			<Button variant="ghost" size="sm" onPress={() => navigate(AUTH_ROUTES.login)}>
				{t("top_bar.sign_in")}
			</Button>
			<Button variant="primary" size="sm" onPress={() => navigate(AUTH_ROUTES.login)}>
				{t("top_bar.join_cta")}
			</Button>
		</div>
	);

	return (
		<div className="flex min-h-screen flex-col bg-background">
			{!hideTopBar && (
				<TopBar
					withBrand
					nav={nav}
					actions={actions}
					user={topBarUser}
					hideSettings={hideSettingsItem}
					hideUserMenuOnMobile
					extraItems={topBarUser ? extraItems : undefined}
					onMenuClick={() => setIsMenuOpen(true)}
					sticky
				/>
			)}
			<main className="flex-1">
				<Outlet />
			</main>
			<Drawer.Backdrop isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
				<Drawer.Content placement="right">
					<Drawer.Dialog>
						<Drawer.CloseTrigger />
						<Drawer.Header>
							<Drawer.Heading>{t("nav.menu")}</Drawer.Heading>
						</Drawer.Header>
						<Drawer.Body>
							<nav className="flex flex-col gap-1">
								{NAV_PATHS.map((path) => {
									const isActive = path === selectedKey;
									return (
										<Button
											key={path}
											variant={isActive ? "primary" : "ghost"}
											onClick={() => goAndClose(navTargetFor(path))}
											className={clsx(
												"w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors",
												isActive
													? "bg-surface font-semibold text-primary-600"
													: "text-muted-foreground hover:bg-surface hover:text-primary-600",
											)}
										>
											{t(NAV_LABEL_KEY[path])}
										</Button>
									);
								})}
							</nav>
						</Drawer.Body>
						<Drawer.Footer className="flex flex-col gap-2">
							{topBarUser ? (
								<>
									{extraItems.map((item) => {
										const Icon = item.icon;
										return (
											<Button key={item.id} className="w-full justify-start gap-2" variant="ghost" onPress={() => goAndClose(item.to)}>
												{Icon ? <Icon className="size-4" aria-hidden /> : null}
												{item.label}
											</Button>
										);
									})}
									{hideSettingsItem ? null : (
										<Button className="w-full justify-start gap-2" variant="ghost" onPress={() => goAndClose(SETTINGS_ROUTES.settings)}>
											<Settings className="size-4" aria-hidden />
											{t("nav.settings")}
										</Button>
									)}
									<Button className="w-full justify-start gap-2 text-danger" variant="ghost" onPress={signOutAndClose}>
										<LogOut className="size-4" aria-hidden />
										{t("top_bar.sign_out")}
									</Button>
								</>
							) : (
								<>
									<Button className="w-full" variant="ghost" onPress={() => goAndClose(AUTH_ROUTES.login)}>
										{t("top_bar.sign_in")}
									</Button>
									<Button className="w-full" variant="primary" onPress={() => goAndClose(AUTH_ROUTES.login)}>
										{t("top_bar.join_cta")}
									</Button>
								</>
							)}
							<div className="border-t border-border pt-1">
								<ThemeSwitcher />
							</div>
						</Drawer.Footer>
					</Drawer.Dialog>
				</Drawer.Content>
			</Drawer.Backdrop>
		</div>
	);
}
